import type { PublicBillingLedgerItem, WorkflowRunSummary } from "@readxx/contracts";
import { AlertTriangle, LoaderCircle, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "../../shared/ui/button.js";
import { EmptyState } from "../../shared/ui/empty-state.js";

export function operationStatusLabel(status: WorkflowRunSummary["status"]) {
  return (
    (
      {
        queued: "排队中",
        running: "运行中",
        cancel_requested: "取消中",
        committing: "提交中",
        succeeded: "已完成",
        failed: "失败",
        canceled: "已取消",
      } as Record<WorkflowRunSummary["status"], string>
    )[status] ?? status
  );
}
export function Loading({ compact = false }: { compact?: boolean }) {
  return (
    <output
      className={`grid ${compact ? "min-h-28" : "min-h-52"} place-items-center text-sm text-muted-foreground`}
    >
      <span className="inline-flex items-center gap-2">
        <LoaderCircle className="size-4 animate-spin" />
        正在加载管理数据
      </span>
    </output>
  );
}
export function InlineFailure({ error }: { error: Error }) {
  return (
    <div className="flex min-h-28 items-center gap-2 px-4 py-5 text-sm text-destructive">
      <AlertTriangle className="size-4 shrink-0" />
      <span className="line-clamp-2">{error.message}</span>
    </div>
  );
}
export function InlineEmpty({ text }: { text: string }) {
  return (
    <div className="grid min-h-28 place-items-center px-4 py-5 text-sm text-muted-foreground">
      {text}
    </div>
  );
}
export function Failure({ error, retry }: { error: Error; retry: () => void }) {
  return (
    <EmptyState
      title="管理数据加载失败"
      description={error.message}
      action={{ label: "重试", onClick: retry, variant: "outline" }}
    />
  );
}
export function RefreshButton({ fetching, onClick }: { fetching: boolean; onClick: () => void }) {
  return (
    <Button variant="outline" disabled={fetching} onClick={onClick}>
      <RefreshCw className={fetching ? "animate-spin" : ""} />
      刷新
    </Button>
  );
}
export function formatInteger(value: number) {
  return new Intl.NumberFormat("zh-CN", {
    notation: value > 9999 ? "compact" : "standard",
    maximumFractionDigits: 1,
  }).format(value);
}
export function formatProviderCost(micros: string, currency = "CNY") {
  const n = BigInt(micros);
  const whole = (n / 1_000_000n).toLocaleString("zh-CN");
  const decimal = (n % 1_000_000n).toString().padStart(6, "0").replace(/0+$/, "").padEnd(2, "0");
  return `${currency === "CNY" ? "¥" : `${currency} `}${whole}.${decimal}`;
}
export function formatCost(value: string) {
  return formatProviderCost(value);
}
export function formatGrossMargin(value: number | null) {
  return value === null
    ? "—"
    : `${(value / 100).toLocaleString("zh-CN", { maximumFractionDigits: 2 })}%`;
}
export function profitabilityBasisLabel(
  value: "none" | "estimated" | "mixed" | "reconciled" | "incomplete-unpriced",
) {
  return {
    none: "暂无 Provider 成本记录",
    estimated: "全部为本地价格估算",
    mixed: "部分供应商成本已对账",
    reconciled: "全部供应商成本已对账",
    "incomplete-unpriced": "含未定价 MinerU 配额用量",
  }[value];
}
export function cnyToMicros(value: string): string | null {
  const matched = /^(\d+)(?:\.(\d{0,6}))?$/.exec(value.trim());
  if (!matched) return null;
  try {
    const whole = BigInt(matched[1] ?? "0");
    const fraction = BigInt((matched[2] ?? "").padEnd(6, "0"));
    return (whole * 1_000_000n + fraction).toString();
  } catch {
    return null;
  }
}
export function microsToCnyInput(value: string) {
  try {
    const micros = BigInt(value);
    const whole = micros / 1_000_000n;
    const fraction = (micros % 1_000_000n).toString().padStart(6, "0").replace(/0+$/, "");
    return fraction ? `${whole}.${fraction}` : whole.toString();
  } catch {
    return "";
  }
}
export function formatCredits(value: string) {
  try {
    return new Intl.NumberFormat("zh-CN").format(BigInt(value));
  } catch {
    return value;
  }
}

export function formatMiB(value: string) {
  return `${(Number(value) / (1024 * 1024)).toFixed(1)} MiB`;
}
export function billingServiceLabel(value: PublicBillingLedgerItem["serviceKey"]) {
  return (
    {
      "import-text": "资料导入",
      "outline-generation": "资料结构生成",
      "document-content-generation": "正文生成",
      "translation-standard": "文档翻译",
      "ai-answer": "问 AI",
      "document-guide": "AI 导读",
      "card-extraction": "知识卡提取",
      "knowledge-profile": "知识画像",
    } as Record<PublicBillingLedgerItem["serviceKey"], string>
  )[value];
}
export function purposeLabel(value: string) {
  return (
    (
      {
        "document-guide": "AI 导读",
        "document-ai-answer": "问 AI",
        "document-translation": "文档翻译",
        "knowledge-card-extract": "知识卡提取",
      } as Record<string, string>
    )[value] ?? value
  );
}
export function formatDate(value: string) {
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}
export function useDebounced<T>(value: T, delay: number) {
  const [result, setResult] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setResult(value), delay);
    return () => window.clearTimeout(timer);
  }, [delay, value]);
  return result;
}
